import React, { useEffect, useState, useCallback } from 'react'
import { Helmet } from 'react-helmet'
import { message, Spin } from 'antd'
import { inject, observer } from 'mobx-react'
import { ArrowLeftOutlined } from '@ant-design/icons'
import DashboardLayout from '../../layouts/DashboardLayout'
import PageTitle from '../../components/PageTitle'
import PostCard from './components/PostCard/PostCard'
import SkeletonPost from './components/SkeletonPost/SkeletonPost'
import { useParams, Link } from 'react-router-dom'
import styled from 'styled-components'
import { colors } from './NewsFeedStyled'

const PageContainer = styled.div`
  background: ${colors.bg};
  min-height: calc(100vh - 130px);
  padding: 24px 0;
  width: 100%;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica,
    Arial, sans-serif;
`

const DetailWrapper = styled.div`
  max-width: 680px;
  margin: 0 auto;
  padding: 16px 8px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(8px);
  border: 1px solid #e4e6eb;
  color: #050505;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s ease;
  width: fit-content;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

  &:hover {
    background: #e4e6eb;
    color: #1877f2;
    transform: translateX(-4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e4e6eb;
  text-align: center;
  gap: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02);

  h3 {
    font-size: 18px;
    font-weight: 700;
    color: #050505;
    margin: 0;
  }

  p {
    color: #65676b;
    font-size: 14px;
    margin: 0;
  }
`

const NewsFeedDetailPage = props => {
  const { newsFeedStore, authenticationStore, userStore } = props
  const { id } = useParams() || props.match?.params || {}
  const currentUser = authenticationStore?.currentUser

  const [post, setPost] = useState(null)
  const [loading, setLoading] = useState(true)

  // Load user lists once (store dedupe nếu đã có sẵn từ NewsFeedPage)
  useEffect(() => {
    if (!userStore) return
    userStore.getUserListV2().catch(() => {})
    userStore.getMentionUserList().catch(() => {})
  }, [userStore])

  const fetchPost = useCallback(async () => {
    if (!id) return
    try {
      setLoading(true)
      const res = await newsFeedStore.getPostById(id)
      if (res) {
        setPost(res)
      } else {
        message.error('Không tìm thấy bài viết!')
      }
    } catch (err) {
      console.error('Fetch post detail error:', err)
      message.error(err?.vi || 'Lỗi khi tải chi tiết bài viết!')
    } finally {
      setLoading(false)
    }
  }, [id, newsFeedStore])

  useEffect(() => {
    fetchPost()
  }, [fetchPost])

  return (
    <DashboardLayout title="Chi tiết bài viết">
      <Helmet>
        <title>Chi tiết bài viết | Bảng tin</title>
      </Helmet>
      <PageTitle
        location={props.location}
        title="Chi tiết bài viết"
        hiddenGoBack={true}
      />

      <PageContainer>
        <DetailWrapper>
          <BackButton to="/utility/newsfeed">
            <ArrowLeftOutlined /> Quay lại Bảng tin
          </BackButton>

          {loading ? (
            <SkeletonPost />
          ) : !post ? (
            <ErrorContainer>
              <div style={{ fontSize: 48 }}>🔍</div>
              <h3>Bài viết không tồn tại</h3>
              <p>Bài viết này có thể đã bị xóa hoặc bạn không có quyền xem.</p>
              <BackButton to="/utility/newsfeed">Quay lại Bảng tin</BackButton>
            </ErrorContainer>
          ) : (
            <PostCard
              post={post}
              currentUser={currentUser}
              newsFeedStore={newsFeedStore}
              authenticationStore={authenticationStore}
              defaultShowComments={true} // Detail page always shows comments
            />
          )}
        </DetailWrapper>
      </PageContainer>
    </DashboardLayout>
  )
}

export default inject(
  'newsFeedStore',
  'authenticationStore',
  'userStore'
)(observer(NewsFeedDetailPage))
